import { useEffect } from 'react';

// Reveal-on-scroll. Any descendant of `.vt-scope` carrying `data-reveal` fades/slides
// in the first time it enters the viewport; an optional `data-delay` (e.g. "80ms")
// staggers it. Re-runs its scan when `deps` change so freshly rendered nodes (after a
// route/layout switch) are picked up too.
//
// Two things make this resilient:
//  1. The "already handled" marker is the `.rv` CLASS (set only when an element is
//     actually revealed), not a flag set at observe-time. Under React 18 StrictMode the
//     first observer is disconnected before it can fire, so on the second effect run we
//     must re-observe everything that isn't revealed yet — querying `:not(.rv)` does
//     exactly that. (A flag set at observe-time would wrongly skip them and leave the
//     page blank.)
//  2. A fail-safe timer force-reveals any straggler so a missed observer callback can
//     never leave content permanently invisible.
export function useReveal(deps = []) {
  useEffect(() => {
    const selector = '.vt-scope [data-reveal]:not(.rv)';
    const els = Array.from(document.querySelectorAll(selector));
    if (els.length === 0) return undefined;

    // Apply each element's stagger delay once.
    els.forEach((el) => {
      if (el.dataset.delay && el.style.transitionDelay !== el.dataset.delay) {
        el.style.transitionDelay = el.dataset.delay;
      }
    });

    // No IntersectionObserver support -> just show everything.
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('rv'));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('rv');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.15 }
    );

    els.forEach((el) => io.observe(el));

    // Safety net: content must never stay hidden. If any element hasn't revealed a few
    // seconds in (observer never fired, tab was backgrounded, etc.), show it.
    const failSafe = setTimeout(() => {
      document.querySelectorAll(selector).forEach((el) => el.classList.add('rv'));
    }, 2500);

    return () => {
      clearTimeout(failSafe);
      io.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}